import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getSessionID, isLoggedIn } from 'ducks/auth';
import * as chat from 'ducks/chat';
import * as globalContext from 'ducks/globalContext';
import config from 'config';

const RECONNECT_STEP_MS = 3000;
const RECONNECT_MAX_MS = 30000;
// Server idle timeout is 5 min; ping every 4 min so the session never goes silent.
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000;
const WS_PATH = '/api/common/chat/listen';

const buildWebSocketURL = (branchID) => {
  const base = config.serverURL
    ? config.serverURL.replace(/^http(s?)/, 'ws$1')
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
  const query = branchID ? `?branchID=${encodeURIComponent(branchID)}` : '';
  return `${base}${WS_PATH}${query}`;
};

export const useChatSocket = (enabled = true) => {
  const {
    requestChannels,
    newMessageReceived,
    messageReadReceived,
    messageEditedReceived,
    messageDeletedReceived,
  } = bindActionCreators(chat.actions, useDispatch());
  const sessionID = useSelector(getSessionID);
  const loggedIn = useSelector(isLoggedIn);
  const branchID = useSelector((state) => globalContext.selectors(state)?.branchID);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !loggedIn || !sessionID) return undefined;

    let cancelled = false;
    let ws = null;
    let reconnectTimer = null;
    let reconnectDelay = RECONNECT_STEP_MS;
    let heartbeatTimer = null;
    hasConnectedRef.current = false;

    const stopHeartbeat = () => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      heartbeatTimer = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch (err) {
            // ignore — onclose will fire and trigger reconnect
          }
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      reconnectTimer = setTimeout(connect, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay + RECONNECT_STEP_MS, RECONNECT_MAX_MS);
    };

    const connect = () => {
      if (cancelled) return;

      ws = new WebSocket(buildWebSocketURL(branchID));

      ws.onopen = () => {
        reconnectDelay = RECONNECT_STEP_MS;
        if (hasConnectedRef.current) {
          requestChannels();
        }
        hasConnectedRef.current = true;
        startHeartbeat();
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          return;
        }
        if (!data || !data.type) return;

        if (data.type === 'new_message' && data.payload) {
          newMessageReceived(data.payload);
        } else if (data.type === 'message_read' && data.payload) {
          messageReadReceived(data.payload);
        } else if (data.type === 'message_edited' && data.payload) {
          messageEditedReceived(data.payload);
        } else if (data.type === 'message_deleted' && data.payload) {
          messageDeletedReceived(data.payload);
        }
      };

      ws.onerror = () => {
        console.warn('[Chat WS] Connection error');
      };

      ws.onclose = () => {
        stopHeartbeat();
        ws = null;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      stopHeartbeat();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        ws.onclose = null;
        ws.close();
        ws = null;
      }
    };
  }, [enabled, loggedIn, sessionID, branchID]);
};
